<script setup lang="ts">
import { ref } from 'vue';
import { CATEGORY_LABEL } from './issue-enums';

interface Props {
  current: number;
  disabled?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{ change: [category: number] }>();

const isOpen = ref(false);
const CATEGORIES = [0, 1, 2, 3, 4, 5, 6] as const;

function select(c: number) {
  if (c !== props.current) emit('change', c);
  isOpen.value = false;
}
</script>

<template>
  <div class="relative inline-block">
    <button
      :disabled="disabled"
      class="flex items-center gap-1.5 px-2 py-1 border border-border rounded-md bg-surface hover:bg-canvas-soft transition-colors text-caption disabled:opacity-50 disabled:pointer-events-none"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @click="isOpen = !isOpen"
    >
      <span class="text-fg">{{ CATEGORY_LABEL[current] ?? String(current) }}</span>
      <svg class="w-3.5 h-3.5 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      v-if="isOpen"
      role="listbox"
      class="absolute top-full left-0 mt-1 z-20 bg-surface border border-border rounded-lg shadow-lg py-1 min-w-[160px]"
    >
      <button
        v-for="c in CATEGORIES"
        :key="c"
        role="option"
        :aria-selected="c === current"
        class="w-full flex items-center gap-2 px-3 py-1.5 text-caption hover:bg-canvas-soft transition-colors text-left"
        @click="select(c)"
      >
        {{ CATEGORY_LABEL[c] }}
        <svg v-if="c === current" class="w-3 h-3 text-navy-600 ml-auto" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <div v-if="isOpen" class="fixed inset-0 z-10" aria-hidden="true" @click="isOpen = false" />
  </div>
</template>
